import React, { useEffect, useState, useMemo } from 'react';
import {
  Table,
  Button,
  Space,
  message,
  Typography,
  Tag,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { taskTemplateService, TaskTemplate } from '../services/taskTemplates';
import { useTableColumnSearch } from '../hooks/useTableColumnSearch';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { ResizableTableHeader } from '../components/ResizableTableHeader';
import { ColumnVisibilityToggle } from '../components/ColumnVisibilityToggle';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import '../styles/resizable-table.css';

const { Title } = Typography;

export const TaskTemplates: React.FC = () => {
  const [taskTemplates, setTaskTemplates] = useState<TaskTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canView = hasPermission(user, 'task-templates.view');
  const canCreate = hasPermission(user, 'task-templates.create');

  useEffect(() => {
    if (!canView) {
      setTaskTemplates([]);
      return;
    }
    fetchData();
  }, [canView]);

  const fetchData = async () => {
    if (!canView) {
      return;
    }
    setLoading(true);
    try {
      const response = await taskTemplateService.getAll();
      setTaskTemplates(response.data || []);
    } catch {
      message.error('Failed to load task templates data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    navigate('/task-templates/new');
  };

  const handleEdit = (taskTemplate: TaskTemplate) => {
    if (!canView) {
      return;
    }
    navigate(`/task-templates/${taskTemplate.id}/edit`);
  };


  const baseColumns = [
    { 
      title: 'Name', 
      dataIndex: 'name', 
      key: 'name', 
      width: 200,
      sorter: (a: TaskTemplate, b: TaskTemplate) => (a.name || '').localeCompare(b.name || ''),
    },
    {
      title: 'Task Type',
      dataIndex: 'task_type',
      key: 'task_type',
      width: 150,
      render: (taskType: any, record: TaskTemplate) => {
        // Backend returns 'task_type' (snake_case), but TypeScript interface uses 'taskType' (camelCase)
        const type = taskType || record.taskType || (record as any).task_type;
        return type?.name || '-';
      },
      sorter: (a: TaskTemplate, b: TaskTemplate) => {
        const aType = a.taskType || (a as any).task_type;
        const bType = b.taskType || (b as any).task_type;
        const aName = aType?.name || '';
        const bName = bType?.name || '';
        return aName.localeCompare(bName);
      },
    },
    {
      title: 'Enabled',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 120,
      sorter: (a: TaskTemplate, b: TaskTemplate) => (a.enabled ? 1 : 0) - (b.enabled ? 1 : 0),
      render: (enabled: boolean) => (
        <Tag color={enabled ? 'green' : 'red'}>{enabled ? 'Yes' : 'No'}</Tag>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      sorter: (a: TaskTemplate, b: TaskTemplate) => {
        if (!a.created_at && !b.created_at) return 0;
        if (!a.created_at) return 1;
        if (!b.created_at) return -1;
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      },
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Updated At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 150,
      sorter: (a: TaskTemplate, b: TaskTemplate) => {
        if (!a.updated_at && !b.updated_at) return 0;
        if (!a.updated_at) return 1;
        if (!b.updated_at) return -1;
        return new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime();
      },
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
  ];

  const {
    columnConfig,
    visibleColumns,
    visibleKeys,
    handleColumnVisibilityChange,
  } = useColumnVisibility(baseColumns);

  const { columns: resizableColumns } = useResizableColumns(visibleColumns);

  const memoizedColumns = useMemo(
    () =>
      resizableColumns.map((col) => ({
        ...col,
        width: col.width, // Preserve width
        onHeaderCell: col.onHeaderCell, // Preserve onHeaderCell for resizing
        getSearchValue:
          col.dataIndex === 'name'
            ? (record: TaskTemplate) => record.name
            : col.dataIndex === 'task_type'
            ? (record: TaskTemplate) => {
                const type = record.taskType || (record as any).task_type;
                return type?.name || '';
              }
            : col.dataIndex === 'enabled'
            ? (record: TaskTemplate) => (record.enabled ? 'Yes' : 'No')
            : col.dataIndex === 'created_at'
            ? (record: TaskTemplate) => record.created_at
            : col.dataIndex === 'updated_at'
            ? (record: TaskTemplate) => record.updated_at
            : (undefined as unknown as (record: TaskTemplate) => string | number | null | undefined),
      })),
    [resizableColumns]
  );

  const { columnsWithSearch, filteredData } = useTableColumnSearch<TaskTemplate>(taskTemplates, memoizedColumns as any);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={3} style={{ margin: 0 }}>
            Task Templates
          </Title>
          <Space>
            <ColumnVisibilityToggle
              columns={columnConfig}
              visibleKeys={visibleKeys}
              onChange={handleColumnVisibilityChange}
            />
            {canCreate && (
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                New Task Template
              </Button>
            )}
          </Space>
        </div>

        <Table
          columns={columnsWithSearch}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          pagination={false}
          components={{
            header: {
              cell: ResizableTableHeader,
            },
          }}
          footer={() => (
            <div style={{ textAlign: 'left', padding: '8px 0' }}>
              Total: {filteredData.length} {filteredData.length === 1 ? 'task template' : 'task templates'}
            </div>
          )}
          onRow={(record) => ({
            onClick: canView ? () => handleEdit(record) : undefined,
            style: { cursor: canView ? 'pointer' : 'default' },
          })}
        />
    </div>
  );
};
